'use client';

import { AB_KEYS, isVariant, refreshExperiments } from '@/lib/abExperiments';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { Endpoints } from '@/lib/Endpoints';
import { setBasket } from '@/lib/BasketStorage';
import { Requester } from '@/lib/Requester';
import { WebLink } from '@/lib/WebLinks';
import { Basket } from '@/models/Basket';
import { GenericResponse } from '@/models/GenericResponse';
import { Plane } from 'lucide-react';
import { useRouter } from 'next/navigation';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';

const SESSION_KEY = 'hzc-thy-miles';
const MILE_BLOCK = 1000;
const TL_BLOCK = 300;

function milesForOrder(orderTotal: number, mileBlock = MILE_BLOCK, tlBlock = TL_BLOCK) {
  const kurus = Math.round((Number(orderTotal || 0) + Number.EPSILON) * 100);
  if (kurus <= 0 || mileBlock <= 0 || tlBlock <= 0) return 0;
  return Math.ceil((kurus * mileBlock) / (tlBlock * 100));
}

function tlForMiles(miles: number, mileBlock = MILE_BLOCK, tlBlock = TL_BLOCK) {
  const qty = Math.max(0, Math.floor(Number(miles) || 0));
  if (mileBlock <= 0) return 0;
  return Math.round((qty * tlBlock * 100) / mileBlock) / 100;
}

export type MilesIssueOutcome = 'completed' | 'remainder' | 'none' | 'failed';

export type MilesSmilesHandle = {
  issuePending: () => Promise<MilesIssueOutcome>;
  hasMilesIntent: () => boolean;
};

type MemberQuote = {
  ffid: string;
  totalMiles: number;
  quotedMaxMiles: number;
  orderTotal: number;
  mileBlock?: number;
  tlBlock?: number;
  name?: string | null;
  surname?: string | null;
};

type IssueBody = {
  basket?: Basket | null;
  miles?: { quantity?: number; milesTl?: number; issuedAt?: string; ffid?: string };
  card?: { amount?: number };
  completed?: boolean;
};

type Session = { token: string; ffid: string };

function milesAllowed() {
  if (process.env.NEXT_PUBLIC_THY_MILES_ENABLED === '0') return false;
  return isVariant(AB_KEYS.thyIntegration, 'b');
}

export function useShowThyMiles() {
  const [show, setShow] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (!cancelled) setShow(milesAllowed());
    };
    void refreshExperiments().finally(() => {
      sync();
      if (!cancelled) setReady(true);
    });
    window.addEventListener('hzc:ab-experiments-updated', sync);
    return () => {
      cancelled = true;
      window.removeEventListener('hzc:ab-experiments-updated', sync);
    };
  }, []);
  return { show, ready };
}

function loginOrigin() {
  const url = process.env.NEXT_PUBLIC_THY_MILES_LOGIN_URL;
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

function openIssue(basket?: Basket) {
  const miles = basket?.paymentInformation?.miles;
  if (!miles?.issuedAt || miles.reversedAt) return null;
  return miles;
}

function readSession(): Session | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed?.token || !parsed?.ffid) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session: Session | null) {
  if (typeof window === 'undefined') return;
  if (!session) {
    sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function money(value: number) {
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value);
}

function parseLoginMessage(data: unknown): { token: string; ffid: string } | null {
  let payload = data;
  if (typeof data === 'string') {
    try {
      payload = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as {
    isSuccess?: boolean;
    token?: string;
    access_token?: string;
    accessToken?: string;
    ffid?: string;
    memberId?: string;
    memberID?: string;
    flyerId?: string;
  };
  if (body.isSuccess === false) return null;
  const token = String(body.token || body.access_token || body.accessToken || '').trim();
  const ffid = String(body.ffid || body.memberId || body.memberID || body.flyerId || '').trim();
  if (!token || !ffid) return null;
  return { token, ffid };
}

function withMilesRails(current: Basket | undefined, result: IssueBody, quantity: number) {
  if (!current) return undefined;
  const miles = {
    ...(current.paymentInformation?.miles || {}),
    ...(result.miles || {}),
  };
  if (!miles.issuedAt) miles.issuedAt = new Date().toISOString();
  if (miles.quantity == null) miles.quantity = quantity;
  if (miles.milesTl == null) miles.milesTl = tlForMiles(quantity);
  const card = {
    ...(current.paymentInformation?.card || {}),
    ...(result.card || {}),
  };
  return {
    ...current,
    paymentInformation: {
      ...(current.paymentInformation || {}),
      miles,
      card,
    },
  };
}

export const CheckoutMilesSmiles = forwardRef<
  MilesSmilesHandle,
  {
    basket?: Basket;
    onCardAmount: (amount: number | null) => void;
    onRemainder?: (amount: number) => void;
    onBasket?: (basket: Basket) => void;
    onMilesDraft?: (miles: number) => void;
    cardBelow?: boolean;
  }
>(function CheckoutMilesSmiles({
  basket, onCardAmount, onRemainder, onBasket, onMilesDraft, cardBelow,
}, ref) {
  const router = useRouter();
  const { toast } = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<MemberQuote | null>(null);
  const [milesInput, setMilesInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState('');
  const [issuedQty, setIssuedQty] = useState<number | null>(null);
  const [cardAmount, setCardAmount] = useState<number | null>(null);
  const memberRef = useRef(member);
  const inputRef = useRef(milesInput);
  const issuedRef = useRef(issuedQty);
  const sessionRef = useRef(session);
  const cardRef = useRef(cardAmount);
  const basketRef = useRef(basket);
  const popupRef = useRef<Window | null>(null);
  const connectLock = useRef(false);
  const restoredRef = useRef(false);
  const issueFlight = useRef<Promise<MilesIssueOutcome> | null>(null);
  const { show: showMiles } = useShowThyMiles();
  memberRef.current = member;
  inputRef.current = milesInput;
  issuedRef.current = issuedQty;
  sessionRef.current = session;
  cardRef.current = cardAmount;
  basketRef.current = basket;

  const applyCard = (amount: number | null) => {
    setCardAmount(amount);
    cardRef.current = amount;
    onCardAmount(amount);
  };

  const connect = async (token: string, ffid: string, options?: { silent?: boolean }) => {
    if (connectLock.current) return;
    connectLock.current = true;
    setBusy(true);
    setWarning('');
    try {
      const { data } = await Requester.post<GenericResponse<MemberQuote>>(
        Endpoints.MILES_SMILES_MEMBER,
        { token, ffid },
      );
      const quote = data.data;
      const next = { token, ffid: quote.ffid || ffid };
      writeSession(next);
      setSession(next);
      sessionRef.current = next;
      setMember(quote);
      memberRef.current = quote;
    } catch (error) {
      const { statusCode, message } = ErrorHandler.getMessage(error);
      if (statusCode === 8306) writeSession(null);
      if (!options?.silent) {
        toast({ variant: 'destructive', description: message });
      }
    } finally {
      connectLock.current = false;
      setBusy(false);
    }
  };

  const connectRef = useRef(connect);
  connectRef.current = connect;

  useEffect(() => {
    const origin = loginOrigin();
    if (!origin) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const login = parseLoginMessage(event.data);
      if (!login) return;
      popupRef.current?.close();
      popupRef.current = null;
      void connectRef.current(login.token, login.ffid);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!showMiles || !basket?.orderId || restoredRef.current || memberRef.current || openIssue(basket)) return;
    const stored = readSession();
    if (!stored) return;
    restoredRef.current = true;
    void connectRef.current(stored.token, stored.ffid, { silent: true });
  }, [showMiles, basket?.orderId, basket?.paymentInformation?.miles?.issuedAt]);

  useEffect(() => {
    if (!showMiles) return;
    const existing = openIssue(basket);
    if (!existing) return;
    setIssuedQty(Number(existing.quantity) || 0);
    issuedRef.current = Number(existing.quantity) || 0;
    const amount = basket?.paymentInformation?.card?.amount;
    if (amount != null) applyCard(Number(amount));
    const stored = readSession();
    if (stored) setSession(stored);
    // existing issue already on the basket; card amount comes from it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMiles, basket?.orderId, basket?.paymentInformation?.miles?.issuedAt]);

  const openLogin = () => {
    const url = process.env.NEXT_PUBLIC_THY_MILES_LOGIN_URL;
    if (!url || !loginOrigin()) {
      toast({ description: 'Miles&Smiles girişi şu an kullanılamıyor.' });
      return;
    }
    popupRef.current?.close();
    const popup = window.open(url, 'thy-miles-login', 'width=480,height=720');
    if (!popup) {
      toast({ description: 'Giriş penceresi açılamadı. Tarayıcı pencere engelini kapatıp tekrar dene.' });
      return;
    }
    popupRef.current = popup;
  };

  const issue = async (quantity: number, payAll: boolean): Promise<MilesIssueOutcome> => {
    if (issuedRef.current != null) {
      return (cardRef.current || 0) > 0 ? 'remainder' : 'completed';
    }
    const quote = memberRef.current;
    const active = sessionRef.current;
    if (!payAll && quantity < 1) return 'none';
    if (!quote || !active) {
      toast({ description: 'Önce Miles&Smiles hesabını bağla.' });
      return 'failed';
    }
    const orderTotal = Number(quote.orderTotal);
    const orderMiles = milesForOrder(orderTotal, quote.mileBlock, quote.tlBlock);
    if (payAll && quote.quotedMaxMiles < orderMiles) {
      const text = `Bakiyeniz siparişi karşılamıyor. ${money(quote.totalMiles)} miliniz var, sipariş ${money(orderMiles)} mil.`;
      setWarning(text);
      toast({ description: text });
      return 'failed';
    }
    const spend = payAll ? quote.quotedMaxMiles : quantity;
    if (spend < 1 || spend > quote.quotedMaxMiles) {
      toast({ description: `1 ile ${quote.quotedMaxMiles} mil arasında gir.` });
      return 'failed';
    }
    setBusy(true);
    setWarning('');
    try {
      const { data } = await Requester.post<GenericResponse<IssueBody>>(
        Endpoints.MILES_SMILES_ISSUE,
        { token: active.token, ffid: active.ffid, milesToSpend: spend },
      );
      const result = data.data;
      const qty = Number(result.miles?.quantity || spend);
      setIssuedQty(qty);
      issuedRef.current = qty;
      const amount = Number(result.card?.amount ?? 0);
      applyCard(amount);
      const nextBasket = withMilesRails(basketRef.current, result, qty);
      if (nextBasket) {
        setBasket(nextBasket, 'CheckoutB - miles issue');
        onBasket?.(nextBasket);
      }
      if (result.completed || amount === 0) {
        router.push(WebLink.ORDER_RECEIVED);
        return 'completed';
      }
      return 'remainder';
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({ variant: 'destructive', description: message });
      return 'failed';
    } finally {
      setBusy(false);
    }
  };

  const startIssue = (quantity: number, payAll: boolean) => {
    if (issueFlight.current) return issueFlight.current;
    const run = issue(quantity, payAll).finally(() => {
      if (issueFlight.current === run) issueFlight.current = null;
    });
    issueFlight.current = run;
    return run;
  };

  const applyAndContinue = async (quantity: number, payAll: boolean) => {
    const outcome = await startIssue(quantity, payAll);
    if (outcome === 'remainder') onRemainder?.(cardRef.current ?? 0);
  };

  useImperativeHandle(ref, () => ({
    issuePending: () => startIssue(Math.floor(Number(inputRef.current) || 0), false),
    hasMilesIntent: () => issuedRef.current != null || Math.floor(Number(inputRef.current) || 0) > 0,
  }));

  if (!showMiles) return null;

  const existing = issuedQty != null;
  const orderTotal = member?.orderTotal ?? basket?.total ?? 0;
  const previewMiles = Math.floor(Number(milesInput) || 0);
  const quotedMax = member?.quotedMaxMiles || 0;
  const mileBlock = member?.mileBlock || MILE_BLOCK;
  const tlBlock = member?.tlBlock || TL_BLOCK;
  const previewValid = previewMiles >= 1 && previewMiles <= quotedMax;
  const previewTl = previewValid ? tlForMiles(previewMiles, mileBlock, tlBlock) : 0;
  const previewCard = previewValid
    ? Math.max(0, Math.round((orderTotal - previewTl) * 100) / 100)
    : null;
  const memberName = [member?.name, member?.surname].filter(Boolean).join(' ');

  return (
    <section className="rounded-2xl border border-[#E7E4DC] bg-white p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F2F2F9] text-[#2B3280]">
          <Plane className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[#212121]">THY Miles&Smiles</span>
          <span className="block truncate text-xs text-[#8A8A8A]">
            {member
              ? [member.ffid, memberName].filter(Boolean).join(' · ')
              : 'Milini bu siparişte kullanmak için hesabını bağla.'}
          </span>
        </span>
        {member && !existing ? (
          <span className="shrink-0 rounded-full bg-[#F6F5F1] px-2.5 py-1 text-xs font-semibold text-[#2B3280]">
            {money(member.totalMiles)} mil
          </span>
        ) : null}
      </div>

      {!member && !existing ? (
        <button
          type="button"
          disabled={busy}
          onClick={openLogin}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#2B3280] text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? <Spinner size={18} /> : 'Miles&Smiles Hesabını Bağla'}
        </button>
      ) : null}

      {member && !existing ? (
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-[#F6F5F1] px-3.5 py-3">
            <div>
              <p className="text-[11px] text-[#8A8A8A]">Mevcut Mil</p>
              <p className="text-sm font-semibold text-[#2B3280]">{money(member.totalMiles)}</p>
            </div>
            <div>
              <p className="text-[11px] text-[#8A8A8A]">Kullanılabilir Maks. Mil</p>
              <p className="text-sm font-semibold text-[#2B3280]">{money(quotedMax)}</p>
            </div>
          </div>

          <label className="block text-sm font-medium text-[#212121]" htmlFor="miles-to-spend">
            Kullanılacak mil
            <span className="mt-2 flex h-12 items-center gap-2 rounded-xl border border-[#E7E4DC] px-3">
              <input
                id="miles-to-spend"
                inputMode="numeric"
                value={milesInput}
                disabled={busy}
                onChange={(event) => {
                  setWarning('');
                  const next = event.target.value.replace(/[^\d]/g, '');
                  setMilesInput(next);
                  onMilesDraft?.(Math.floor(Number(next) || 0));
                }}
                placeholder={`1 – ${quotedMax}`}
                className="h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[#B0B0B0]"
              />
              <span className="text-sm text-[#8A8A8A]">Mil</span>
              <button
                type="button"
                disabled={busy || quotedMax < 1}
                onClick={() => {
                  setWarning('');
                  setMilesInput(String(quotedMax));
                  onMilesDraft?.(quotedMax);
                }}
                className="shrink-0 rounded-full bg-[#F2F2F9] px-2.5 py-1 text-xs font-semibold text-[#2B3280] disabled:opacity-50"
              >
                Tamamı
              </button>
            </span>
          </label>

          {previewValid && previewCard != null ? (
            <div className="rounded-xl border border-[#EFEDE6] p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[#212121]">Mil İndirimi</span>
                <span className="inline-flex rounded-full bg-[#D7F25C] px-2.5 py-1 text-[11px] font-semibold text-[#1F3D12]">
                  -{money(previewMiles)} Mil ({money(previewTl)} TL)
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 font-semibold text-[#212121]">
                <span>Karttan Çekilecek</span>
                <span>{money(previewCard)} TL</span>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            disabled={busy || !previewValid}
            onClick={() => {
              void applyAndContinue(previewMiles, false);
            }}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#2B3280] text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? <Spinner size={18} /> : previewCard === 0 ? 'Siparişi Mil ile Tamamla' : 'Milini Uygula & Kartla Öde'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void applyAndContinue(quotedMax, true);
            }}
            className="flex h-12 w-full items-center justify-center rounded-xl border border-[#2B3280] text-sm font-semibold text-[#2B3280] disabled:opacity-60"
          >
            Hepsini Mil ile Öde
          </button>
          {warning ? (
            <p className="rounded-xl bg-[#F6F5F1] px-3.5 py-3 text-sm text-[#9A3412]">{warning}</p>
          ) : (
            <p className="text-xs text-[#8A8A8A]">{money(mileBlock)} mil = {money(tlBlock)} TL. Kalan tutar yalnız kartla ödenir.</p>
          )}
        </div>
      ) : null}

      {existing ? (
        <div className="mt-4 rounded-xl bg-[#F6F5F1] px-3.5 py-3">
          <span className="inline-flex rounded-full bg-[#D7F25C] px-2.5 py-1 text-[11px] font-semibold text-[#1F3D12]">
            -{money(issuedQty || 0)} Mil uygulandı
          </span>
          {(cardAmount || 0) > 0 ? (
            <p className="mt-2 text-sm text-[#212121]">
              Kalan {money(cardAmount || 0)} TL {cardBelow ? 'aşağıdan kartla ödenir.' : 'kartla ödenir.'}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
});
